import { useMemo, useState } from 'react';

import { SMSContentDefinition, SMSSender } from 'modules/graphql/types';
import { FormField } from 'ui/formField';
import { BasicFilterSelect } from 'ui/selects';
import { isDefined } from 'utils/tsHelpers';

import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { getIsEditMode } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useSMSSendersQueryContext } from '../context/SMSSendersQueryContext';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { formatSMSSenders } from '../utils';

export const SMSSenderSelect = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { smsSenders } = useSMSSendersQueryContext();
  const smsData = (liveTemplate?.mediumData || {}) as SMSContentDefinition;
  const currentSenderId = smsData.smsSender?.id;
  const isCurrentValid = !!smsSenders.find((sender) => sender.id === currentSenderId);
  const defaultValues = isCurrentValid ? [smsData.smsSender.id.toString()] : [];
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const memoizedOptions = useMemo(() => {
    const options = isDefined<SMSSender[]>(smsSenders) ? formatSMSSenders(smsSenders) : [];
    return searchTerm
      ? options.filter(
          ({ label, doubleLine }) =>
            label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doubleLine?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : options;
  }, [searchTerm, smsSenders]);

  return (
    <FormField isHorizontalLayout labelText={SETTINGS_DISPLAY_CONTENT.sender.labelText}>
      {isEditModeVisible ? (
        <BasicFilterSelect
          data-test="sms-settings-select-senders"
          defaultValues={defaultValues}
          id="sms-settings-select-senders"
          onSelect={([newSenderId]) => {
            const newSender = smsSenders.find((sender) => sender.id.toString() === newSenderId);
            setCurrentMediumField('smsSender', {
              __typename: 'SMSSender',
              id: parseInt(newSenderId, 10),
              name: newSender?.name || '',
              phoneNumber: newSender?.phoneNumber || '',
              type: newSender?.type || '',
            });
          }}
          onSearchChange={(searchedItem) => setSearchTerm(searchedItem ?? '')}
          disableInternalFiltering
          options={memoizedOptions}
          placeholder={SETTINGS_DISPLAY_CONTENT.sender.placeholder}
          isFullWidth
        />
      ) : (
        <SettingsReadOnlyText data-test="sender-ro" isUnset={!isCurrentValid}>
          {isCurrentValid ? smsData.smsSender?.name : SETTINGS_DISPLAY_CONTENT.unsetText}
        </SettingsReadOnlyText>
      )}
    </FormField>
  );
};
